import { LoadingOverlay } from '@mantine/core';
import { newCOLORS } from '../../styles/colors';
import { blackMark } from './black-mark';
import { coloredMark } from './colored-mark';

/**
 * Inset that confines a viewport-fixed overlay to the Mantine `AppShell` content
 * region instead of letting it cover the whole viewport.
 *
 * Mantine defines the `--app-shell-*-offset` custom properties on `:root` only
 * while an `AppShell` is mounted, so the `0px` fallbacks collapse this back to a
 * full-viewport overlay on the screens that render outside the shell (sign-in,
 * and the `ProtectedRoute` / `SessionBootstrap` gates). Those keep covering
 * everything exactly as they did before.
 *
 * Why this is needed: the overlay renders at `z-index: 400` (Mantine's `overlay`
 * elevation) while `AppShell.Navbar` sits at `calc(100 + 1)` = 101, and
 * `AppShell.Main` spans the full viewport width — it offsets its content with
 * `padding-inline-start` rather than occupying a narrower box. A viewport-fixed
 * overlay therefore painted on top of the sidebar and swallowed every click on
 * it for as long as any page was loading.
 *
 * Note this is deliberately NOT solved with the `scoped` prop: because
 * `AppShell.Main` is the full viewport width, an absolutely positioned overlay
 * inside it still covers the sidebar.
 */
const APP_SHELL_CONTENT_INSET =
  'var(--app-shell-header-offset, 0px) var(--app-shell-aside-offset, 0px) 0px var(--app-shell-navbar-offset, 0px)';

type BrandedLoadingOverlayProps = {
  visible: boolean;
  transitionDuration?: number;
  variant?: 'colored' | 'black';
  darkMode?: boolean;
  /**
   * When true, the overlay uses `position: absolute` so it covers the nearest
   * positioned ancestor instead of the viewport. The ancestor must be
   * `position: relative` (or any non-static value) for absolute positioning to
   * scope correctly. Defaults to `false` for backward compatibility — existing
   * call sites continue to render a viewport-fixed overlay.
   */
  scoped?: boolean;
};

/**
 * Branded variant of Mantine's `LoadingOverlay` that displays the Bloomfilter
 * flower spinner over a translucent backdrop.
 *
 * By default the overlay uses `position: fixed`. Inside the app shell it is
 * confined to the content region (see {@link APP_SHELL_CONTENT_INSET}) so the
 * sidebar and header stay visible and clickable while a page loads; outside the
 * shell it still covers the entire viewport. Pass `scoped` to switch to
 * `position: absolute` so the overlay only covers its parent container — useful
 * for keeping the rest of the page interactive while a single chart or section
 * finishes loading.
 *
 * @param {BrandedLoadingOverlayProps} props - Component props
 * @returns {JSX.Element} The configured Mantine `LoadingOverlay`
 */
function BrandedLoadingOverlay(props: BrandedLoadingOverlayProps) {
  const spinnerVariant = props.variant === 'colored' ? coloredMark : blackMark;
  const backgroundVariant = newCOLORS.white;
  const { transitionDuration = 0, scoped = false, ...restProps } = props;

  // A `scoped` overlay keeps Mantine's own `inset: 0` so it fills its (already
  // positioned) parent — only the viewport-fixed variant needs confining.
  const positionStyle = scoped
    ? { position: 'absolute' as const }
    : { position: 'fixed' as const, inset: APP_SHELL_CONTENT_INSET };

  return (
    <LoadingOverlay
      data-testid="branded-loading-overlay"
      // Lets E2E wait on ONLY the overlays that can actually block the app chrome.
      // A scoped overlay is absolutely positioned inside its own panel and can
      // never cover the sidebar, so waiting it out is pure dead time — and there
      // are 20 of them on routes the navigation journey visits (work-periods,
      // agent-miner, data-management). Deliberately an explicit attribute rather
      // than matching "position: fixed" in the style attribute: that string
      // depends on CSSOM serialisation, and a selector that silently stops
      // matching turns the wait into a no-op — the exact failure this overlay
      // guard already had once.
      data-overlay-scope={scoped ? 'element' : 'viewport'}
      loaderProps={{ children: spinnerVariant }}
      overlayProps={{ backgroundOpacity: 0.75, color: backgroundVariant }}
      transitionProps={{ duration: transitionDuration }}
      style={positionStyle}
      {...restProps}
    />
  );
}

export { APP_SHELL_CONTENT_INSET, BrandedLoadingOverlay };
